import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S6: Management Summary Dashboard API
 * Executive multi-company overview for admin/directors.
 * 
 * GET /api/dashboard/management-summary?ejercicio=2026
 * 
 * Returns:
 * - Companies in loss
 * - Companies with abnormal tax behavior
 * - Monthly financial evolution (all companies)
 * - Risk flags consolidated
 * - Sync health overview
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const now = new Date();
    const ejercicio = (req.query.ejercicio as string) || String(now.getFullYear());
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // 1. Fetch all tax summaries for the fiscal year
    const { data: allSummaries, error: sumError } = await supabaseAdmin
      .from('tax_summary')
      .select('*, clientes(nombre_fiscal, cif, tipo_empresa)')
      .eq('ejercicio_fiscal', ejercicio)
      .order('periodo', { ascending: true });

    if (sumError) throw sumError;
    const summaries = allSummaries || [];

    // 2. Group by cliente_id
    const byClient = new Map<string, any[]>();
    for (const s of summaries) {
      const arr = byClient.get(s.cliente_id) || [];
      arr.push(s);
      byClient.set(s.cliente_id, arr);
    }

    // 3. Companies in loss (gastos > ingresos for the year)
    const companiesInLoss: any[] = [];
    const companiesWithAnomalies: any[] = [];
    const companyOverviews: any[] = [];

    for (const [clienteId, clientSummaries] of byClient) {
      const totalIngresos = clientSummaries.reduce((s: number, r: any) => s + (Number(r.base_imponible_emitidas) || 0), 0);
      const totalGastos = clientSummaries.reduce((s: number, r: any) => s + (Number(r.base_imponible_recibidas) || 0), 0);
      const totalIvaResultado = clientSummaries.reduce((s: number, r: any) => s + (Number(r.iva_resultado) || 0), 0);
      const totalRetenciones = clientSummaries.reduce((s: number, r: any) => s + (Number(r.retenciones_practicadas) || 0), 0);
      const resultado = totalIngresos - totalGastos;

      const clienteInfo = clientSummaries[0]?.clientes || {};
      const overview = {
        cliente_id: clienteId,
        nombre_fiscal: clienteInfo.nombre_fiscal || 'N/A',
        cif: clienteInfo.cif || 'N/A',
        tipo_empresa: clienteInfo.tipo_empresa || 'N/A',
        total_ingresos: Math.round(totalIngresos * 100) / 100,
        total_gastos: Math.round(totalGastos * 100) / 100,
        resultado: Math.round(resultado * 100) / 100,
        margen_neto: totalIngresos > 0 ? Math.round((resultado / totalIngresos) * 10000) / 100 : 0,
        iva_resultado: Math.round(totalIvaResultado * 100) / 100,
        retenciones: Math.round(totalRetenciones * 100) / 100,
        meses_con_datos: clientSummaries.length,
        status: resultado >= 0 ? 'profit' : 'loss',
      };

      companyOverviews.push(overview);

      if (resultado < 0) {
        companiesInLoss.push(overview);
      }

      // Check for month-to-month anomalies
      for (let i = 1; i < clientSummaries.length; i++) {
        const prev = clientSummaries[i - 1];
        const curr = clientSummaries[i];
        const prevIngresos = Number(prev.base_imponible_emitidas) || 0;
        const currIngresos = Number(curr.base_imponible_emitidas) || 0;

        if (prevIngresos > 0) {
          const deviation = ((currIngresos - prevIngresos) / prevIngresos) * 100;
          if (Math.abs(deviation) > 30) {
            companiesWithAnomalies.push({
              cliente_id: clienteId,
              nombre_fiscal: clienteInfo.nombre_fiscal || 'N/A',
              periodo: curr.periodo,
              tipo: deviation > 0 ? 'spike' : 'drop',
              desviacion_pct: Math.round(deviation * 100) / 100,
              valor_actual: currIngresos,
              valor_anterior: prevIngresos,
            });
          }
        }
      }
    }

    // 4. Monthly evolution (aggregate all companies)
    const monthlyAgg = new Map<string, { ingresos: number; gastos: number; iva: number; companies: number }>();
    for (const s of summaries) {
      const m = monthlyAgg.get(s.periodo) || { ingresos: 0, gastos: 0, iva: 0, companies: 0 };
      m.ingresos += Number(s.base_imponible_emitidas) || 0;
      m.gastos += Number(s.base_imponible_recibidas) || 0;
      m.iva += Number(s.iva_resultado) || 0;
      m.companies += 1;
      monthlyAgg.set(s.periodo, m);
    }

    const monthlyEvolution = Array.from(monthlyAgg.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([periodo, data]) => ({
        periodo,
        ingresos: Math.round(data.ingresos * 100) / 100,
        gastos: Math.round(data.gastos * 100) / 100,
        resultado: Math.round((data.ingresos - data.gastos) * 100) / 100,
        iva_resultado: Math.round(data.iva * 100) / 100,
        num_empresas: data.companies,
      }));

    // 5. Sync health (last 7 days)
    const syncCutoff = new Date();
    syncCutoff.setDate(syncCutoff.getDate() - 7);

    const { data: recentSyncs } = await supabaseAdmin
      .from('cegid_sync_runs')
      .select('id, status, sync_type, started_at, records_processed, error_message')
      .gte('started_at', syncCutoff.toISOString())
      .order('started_at', { ascending: false })
      .limit(50);

    const syncHealth = {
      total_runs: (recentSyncs || []).length,
      completed: (recentSyncs || []).filter((s: any) => s.status === 'completed').length,
      failed: (recentSyncs || []).filter((s: any) => s.status === 'failed').length,
      running: (recentSyncs || []).filter((s: any) => s.status === 'running').length,
      last_sync: recentSyncs?.[0] || null,
    };

    // 6. Recent pending alerts
    const { data: pendingAlerts } = await supabaseAdmin
      .from('alert_history')
      .select('id, severity, message, created_at')
      .eq('delivery_status', 'pending')
      .order('created_at', { ascending: false })
      .limit(10);

    // 7. Risk flags
    const riskFlags = [];

    if (companiesInLoss.length > 0) {
      riskFlags.push({
        tipo: 'companies_in_loss',
        severity: 'warning',
        count: companiesInLoss.length,
        mensaje: `${companiesInLoss.length} empresa(s) con resultado negativo en el ejercicio ${ejercicio}.`,
      });
    }

    if (syncHealth.failed > 0) {
      riskFlags.push({
        tipo: 'sync_failures',
        severity: 'danger',
        count: syncHealth.failed,
        mensaje: `${syncHealth.failed} sincronización(es) fallida(s) en los últimos 7 días.`,
      });
    }

    const companiesNoData = companyOverviews.filter(c => c.meses_con_datos < 3);
    if (companiesNoData.length > 0) {
      riskFlags.push({
        tipo: 'insufficient_data',
        severity: 'info',
        count: companiesNoData.length,
        mensaje: `${companiesNoData.length} empresa(s) con menos de 3 meses de datos.`,
      });
    }

    if (companiesWithAnomalies.length > 3) {
      riskFlags.push({
        tipo: 'multiple_anomalies',
        severity: 'warning',
        count: companiesWithAnomalies.length,
        mensaje: `${companiesWithAnomalies.length} anomalía(s) detectada(s) (desviaciones >30%).`,
      });
    }

    return res.status(200).json({
      success: true,
      data: {
        ejercicio,
        generated_at: new Date().toISOString(),
        totals: {
          total_empresas: byClient.size,
          empresas_profit: companyOverviews.filter(c => c.status === 'profit').length,
          empresas_loss: companiesInLoss.length,
          total_ingresos: Math.round(companyOverviews.reduce((s, c) => s + c.total_ingresos, 0) * 100) / 100,
          total_gastos: Math.round(companyOverviews.reduce((s, c) => s + c.total_gastos, 0) * 100) / 100,
          total_resultado: Math.round(companyOverviews.reduce((s, c) => s + c.resultado, 0) * 100) / 100,
        },
        companies: companyOverviews.sort((a, b) => a.resultado - b.resultado),
        companies_in_loss: companiesInLoss.sort((a, b) => a.resultado - b.resultado),
        anomalies: companiesWithAnomalies,
        monthly_evolution: monthlyEvolution,
        risk_flags: riskFlags,
        sync_health: syncHealth,
        pending_alerts: pendingAlerts || [],
        source: 'reporting_tables',
      },
    });
  } catch (error: any) {
    console.error('[Management Summary] Error:', error);
    return res.status(500).json({ error: 'Error al generar resumen ejecutivo.' });
  }
}

export default withAuth(handler, ['admin']);
